import logger from '@/layers/support/monitoring/logger';
import { PatientParam } from '@/types/models';
import { useCallback, useMemo } from 'react';

const LOG_MODULE = '[Hooks / useCancerAutocomplete]';

export interface UseCancerAutocompleteProps {
  query: string;
  onQueryChange: (value: string) => void;
  patientParamsDictionary: PatientParam[];
  shouldShowUploadPromo?: boolean;
  onUploadPromo?: () => void;
  minQueryLength?: number;
}

export const UPLOAD_PROMO_KEY = 'upload';
export const UPLOAD_PROMO_LABEL = 'Upload medical records and get personalized treatment options';

export function useCancerAutocomplete({
  query,
  onQueryChange,
  patientParamsDictionary,
  shouldShowUploadPromo = false,
  onUploadPromo,
  minQueryLength = 2,
}: UseCancerAutocompleteProps) {
  const cancerTypes = patientParamsDictionary.find(
    param => param.slug === 'c3_cancer_type',
  )?.options;
  const cancerSubtypes = patientParamsDictionary.find(
    param => param.slug === 'c3_cancer_subtype',
  )?.options;

  /**
   * Generates the autocomplete options by combining cancer types and subtypes.
   */
  const autocompleteOptions = useMemo(() => {
    if (!cancerTypes || !cancerSubtypes) return ['The list is loading...'];

    const options: string[] = [];

    cancerTypes.forEach(type => {
      options.push(type.option);

      const relatedSubtypes = cancerSubtypes.filter(
        subtype => subtype.parent_option_id === type.id,
      );
      relatedSubtypes.forEach(subtype => {
        const combined = `${type.option}, ${subtype.option}`;
        options.push(combined);
      });
    });

    logger.debug(`${LOG_MODULE} autocompleteOptions`, { autocompleteOptions: options });
    return options;
  }, [cancerTypes, cancerSubtypes]);

  /**
   * Filters the autocomplete options based on the user's query.
   */
  const filteredOptions = useMemo(() => {
    if (!query || query.length < minQueryLength) return [];

    const queryLower = query.toLowerCase();

    // Get all matching options
    const matchingOptions = autocompleteOptions.filter(option =>
      option.toLowerCase().includes(queryLower),
    );

    // Sort options: items starting with the query come first
    return matchingOptions.sort((a, b) => {
      const aStartsWith = a.toLowerCase().startsWith(queryLower);
      const bStartsWith = b.toLowerCase().startsWith(queryLower);

      // If a starts with query but b doesn't, a comes first
      if (aStartsWith && !bStartsWith) return -1;
      // If b starts with query but a doesn't, b comes first
      if (!aStartsWith && bStartsWith) return 1;

      // For items that both start or don't start with query, sort alphabetically
      return a.toLowerCase().localeCompare(b.toLowerCase());
    });
  }, [autocompleteOptions, query, minQueryLength]);

  const handleInputChange = useCallback(
    (value: string) => {
      logger.debug(`${LOG_MODULE} handleInputChange`, { value });

      // Don't update the query if the value is upload promo text
      if (value === UPLOAD_PROMO_LABEL) {
        return;
      }

      onQueryChange(value);
    },
    [onQueryChange],
  );

  /**
   * Handles selection of an autocomplete option.
   */
  const handleSelectionChange = useCallback(
    (key: React.Key | null) => {
      if (key === UPLOAD_PROMO_KEY) {
        logger.debug(`${LOG_MODULE} handleSelectionChange: UPLOAD PROMO selected`);
        if (onUploadPromo) {
          onUploadPromo();
        }
        return;
      }

      if (key === null) {
        logger.debug(`${LOG_MODULE} handleSelectionChange: cleared`);
        onQueryChange('');
        return;
      }

      const selectedValue = key.toString();
      logger.debug(`${LOG_MODULE} handleSelectionChange: regular selection`, {
        selectedValue,
      });
      onQueryChange(selectedValue);
    },
    [onQueryChange, onUploadPromo],
  );

  return {
    autocompleteOptions,
    filteredOptions,
    handleInputChange,
    handleSelectionChange,
    shouldShowUploadPromo,
  };
}
